import React, { useState, useEffect, useCallback, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import { useHistory } from "react-router-dom";

/** ---------- Origin helpers (unify iframe src + postMessage target) ---------- */
const normalizeOrigin = (v) => {
  if (!v) return null;
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

const ENV = process.env.REACT_APP_ENV; // e.g., "local"
const QUERY_UI_DOMAIN = normalizeOrigin(process.env.REACT_APP_QUERY_UI_CLOUDFRONT_DOMAIN);

/**
 * Local dev uses localhost; otherwise use env-provided domain.
 * If the env var is missing in non-local, we fail fast with example.invalid.
 */
const targetOrigin =
  ENV === "local" ? "http://localhost:8081" : (QUERY_UI_DOMAIN || "https://example.invalid");

/** Helper: get current Cognito ID token (still available if you want to keep TOKEN flow) */
async function getToken() {
  // Ensure Amplify session exists before any API call
  await Auth.currentAuthenticatedUser();
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (err) {
    console.error("error getting token", err);
  }
}

function OpenQueryQuery(props) {
  const history = useHistory();
  const jobId = props.match?.params?.jobId;

  const [job, setJob] = useState(null);

  // Auth readiness + duplicate-call guards
  const [ready, setReady] = useState(false);
  const jobsInFlight = useRef(false);

  // --- Auth readiness gate ---
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setJob(null);
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  // --- Fetch the job only when authed ---
  useEffect(() => {
    if (!ready || !jobId) return;
    if (jobsInFlight.current) return;
    jobsInFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser(); // double-check
        const jobs = await API.get("OpenQueryAPI", `/openquery/jobs`);
        if (cancelled) return;
        const resubmitData = (jobs?.requests || []).find((j) => j.id === jobId);
        setJob(resubmitData || null);
      } catch (err) {
        console.error("error fetching openquery jobs", err);
      } finally {
        jobsInFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, jobId]);

  // --- Iframe messaging: token + job context + PROXY for /openquery/jobs ---
  const handleIframeRequests = useCallback(
    (e) => {
      // Only trust messages from the iframe origin we expect
      if (!e || e.origin !== targetOrigin) {
        return;
      }

      const iframeWindow = document.querySelector("iframe")?.contentWindow;
      if (!iframeWindow) return;

      const sendToken = async () => {
        try {
          const token = await getToken();
          // new structured message (but you can still support legacy if needed)
          iframeWindow.postMessage({ type: "TOKEN", token }, targetOrigin);
        } catch (err) {
          console.error("error sending token to iframe", err);
          iframeWindow.postMessage(
            { type: "TOKEN_ERROR", message: err?.message || "Token error" },
            targetOrigin
          );
        }
      };

      const sendQueryPayload = () => {
        // Keep legacy behavior: send the bare job object
        if (job) {
          iframeWindow.postMessage(job, targetOrigin);
        }
      };

      // --- Legacy string-based messages (backward compatible) ---
      if (typeof e.data === "string") {
        if (e.data === "TOKEN_REQUEST") {
          sendToken();
        } else if (e.data === "IFRAME_LOADED") {
          sendQueryPayload();
        } else if (e.data === "QUERY_SUCCESSFULLY_SUBMITTED") {
          history.push("/openquery");
        }
        return;
      }

      // --- New structured messages ---
      const { type } = e.data || {};

      if (type === "TOKEN_REQUEST") {
        sendToken();
      } else if (type === "IFRAME_LOADED") {
        sendQueryPayload();
      } else if (type === "QUERY_SUCCESSFULLY_SUBMITTED") {
        history.push("/openquery");
      } else if (type === "OPENQUERY_START_JOB") {
        // ***** PROXY CALL: parent makes the /openquery/jobs POST, not the iframe *****
        const { categoryId, definition } = e.data || {};

        if (!definition) {
          console.error("OPENQUERY_START_JOB missing definition:", e.data);
          return;
        }

        (async () => {
          try {
            await Auth.currentAuthenticatedUser();
            const response = await API.post("OpenQueryAPI", "/openquery/jobs", {
              body: definition,
            });

            const status =
              (response && response.statusCode) ||
              (response && response.status) ||
              200;

            iframeWindow.postMessage(
              {
                type: "OPENQUERY_START_JOB_RESULT",
                ok: true,
                status,
                body: response,
                categoryId,
              },
              targetOrigin
            );
          } catch (err) {
            console.error("error submitting openquery job from parent", err);

            const status =
              err?.response?.status ||
              err?.response?.statusCode ||
              500;

            const message =
              err?.response?.data?.message ||
              err?.message ||
              "Job submit failed";

            iframeWindow.postMessage(
              {
                type: "OPENQUERY_START_JOB_RESULT",
                ok: false,
                status,
                body: { message },
                categoryId,
              },
              targetOrigin
            );
          }
        })();
      }
    },
    [job, history]
  );

  useEffect(() => {
    window.addEventListener("message", handleIframeRequests);
    return () => window.removeEventListener("message", handleIframeRequests);
  }, [handleIframeRequests]);

  return (
    <div className="card">
      <div
        className="card-header text-light font-weight-bold container-fluid"
      >
        <div className="row">
          <div className="col-12">Query</div>
        </div>
      </div>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 embed-responsive embed-responsive-16by9">
            {job && (
              <iframe
                src={`${targetOrigin}/${job["category-id"]}/index.html`}
                title="Perception Health Report Request"
                className="embed-responsive-item"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OpenQueryQuery;
